import type { LucideIcon } from 'lucide-react';
import {
  Frown,
  HelpCircle,
  Lightbulb,
  LifeBuoy,
  Mail,
  Meh,
  MessageCircle,
  MessagesSquare,
  Phone,
  Share2,
  ShieldAlert,
  Smile,
  Store,
  Users,
  Wand2,
  Handshake,
} from 'lucide-react';
import { formatError } from '@/lib/errors';
import type {
  CustomerFeedbackAiStatus,
  CustomerFeedbackChannel,
  CustomerFeedbackSentiment,
  CustomerFeedbackTaskKind,
} from '@/types/database';

/**
 * Display metadata for Customer Feedback (migration 147). Brand accents
 * stay wayfinding-only — chips and dots, never ambient fills — same
 * rules as features/tickets/model.ts.
 */

export interface ChannelMeta {
  label: string;
  icon: LucideIcon;
}

export const FEEDBACK_CHANNELS: CustomerFeedbackChannel[] = [
  'email',
  'phone',
  'in_person',
  'chat',
  'social',
  'marketplace',
  'other',
];

export const FEEDBACK_CHANNEL_META: Record<CustomerFeedbackChannel, ChannelMeta> = {
  email: { label: 'Email', icon: Mail },
  phone: { label: 'Phone call', icon: Phone },
  in_person: { label: 'In person', icon: Users },
  chat: { label: 'Chat / text', icon: MessageCircle },
  social: { label: 'Social media', icon: Share2 },
  marketplace: { label: 'Marketplace review', icon: Store },
  other: { label: 'Other', icon: MessagesSquare },
};

export interface TaskKindMeta {
  label: string;
  /** One line on what lands in this bucket, for pickers. */
  hint: string;
  icon: LucideIcon;
  /** Chip fill + text. */
  className: string;
}

export const FEEDBACK_TASK_KINDS: CustomerFeedbackTaskKind[] = [
  'customization',
  'product_idea',
  'customer_service',
  'quality_issue',
  'sales_followup',
  'other',
];

export const FEEDBACK_TASK_KIND_META: Record<CustomerFeedbackTaskKind, TaskKindMeta> = {
  customization: {
    label: 'Customization',
    hint: 'A change to an existing product for this customer.',
    icon: Wand2,
    className: 'bg-atmosphere/40 text-atmosphere-dark',
  },
  product_idea: {
    label: 'Future product',
    hint: 'Something we do not make yet that they would buy.',
    icon: Lightbulb,
    className: 'bg-bright-moss/30 text-bright-moss-dark',
  },
  customer_service: {
    label: 'Customer service',
    hint: 'A replacement, refund, callback or fix for this customer.',
    icon: LifeBuoy,
    className: 'bg-info-soft/40 text-info',
  },
  quality_issue: {
    label: 'Quality concern',
    hint: 'A defect or failure pattern worth investigating.',
    icon: ShieldAlert,
    className: 'bg-fresh-salmon/30 text-fresh-salmon-dark',
  },
  sales_followup: {
    label: 'Sales follow-up',
    hint: 'Something the rep promised to send or do.',
    icon: Handshake,
    className: 'bg-secondary text-secondary-foreground',
  },
  other: {
    label: 'Other',
    hint: 'Anything that does not fit above.',
    icon: HelpCircle,
    className: 'bg-muted text-muted-foreground',
  },
};

export interface SentimentMeta {
  label: string;
  icon: LucideIcon;
  className: string;
}

export const FEEDBACK_SENTIMENT_META: Record<CustomerFeedbackSentiment, SentimentMeta> = {
  positive: { label: 'Positive', icon: Smile, className: 'text-bright-moss-dark' },
  neutral: { label: 'Neutral', icon: Meh, className: 'text-muted-foreground' },
  negative: { label: 'Negative', icon: Frown, className: 'text-fresh-salmon-dark' },
  mixed: { label: 'Mixed', icon: Meh, className: 'text-atmosphere-dark' },
};

export const FEEDBACK_AI_STATUS_LABELS: Record<CustomerFeedbackAiStatus, string> = {
  pending: 'Not processed',
  processing: 'Extracting tasks…',
  done: 'Processed',
  failed: 'Processing failed',
};

/** Display name for the customer on an entry: catalog name first, then the typed name. */
export function feedbackCustomerLabel(entry: {
  customer: { name: string } | null;
  customer_name: string | null;
}): string {
  return entry.customer?.name ?? entry.customer_name ?? 'Unknown customer';
}

/** Short machine codes raised by the triggers, translated for toasts. */
const FEEDBACK_ERRORS: Record<string, string> = {
  feedback_not_found: 'That feedback entry no longer exists.',
  feedback_task_already_sent: 'This task has already been sent as a ticket and cannot be changed.',
  'row-level security': 'You do not have access to the Sales section.',
};

export function describeFeedbackError(err: unknown): string {
  const message = formatError(err, '');
  for (const [code, text] of Object.entries(FEEDBACK_ERRORS)) {
    if (message.includes(code)) return text;
  }
  return message || 'Something went wrong.';
}
